/**
 * IXD 셀에 대사 검토용 시각을 입히는 훅. 개념 구분은 `review-style-tokens.ts` 참조
 * (counterpart = 반대편 선택의 대응 셀 / focus = 커서 / status = 데이터 판정).
 *
 * 이 훅은 그중 둘을 같은 scoped <style> 에 함께 주입한다:
 * 1. counterpart — `counterpartCellIds`: 선택한 DSD 셀에 대응하는 IXD 셀에 외곽선.
 * 2. status — `showUnmatchedStatus`: 매칭되는 DSD 셀이 전혀 없는 IXD 값 셀에 상시 면색.
 *    `matchedCellIds`(전체 매칭 IXD 원본 cellId) 의 여집합으로 계산한다.
 *
 * 둘을 한 훅에 두는 이유는 scopeId 와 `<style>` 엘리먼트를 공유하기 때문이다
 * (둘 다 비면 style 을 비운다). useTableCellSelection 과 같은 동적 <style> 패턴을
 * 사용해 셀 재렌더 없이 입히고, 셀렉터는 반환된 scopeId 로 이 컨테이너 안으로만
 * 한정한다 — 같은 cellId 를 쓰는 다른 렌더러(DSD 프리뷰, workbook-match)에 번지지
 * 않게 한다.
 */

import { useEffect, useId } from "react";
// 시각 토큰은 DSD 렌더러(dsd-preview-renderer.tsx)와 공유한다.
import {
  REVIEW_COUNTERPART_CSS,
  REVIEW_NO_MATCH_CSS,
} from "./review-style-tokens";

interface UseIxdCellReviewStylesOptions {
  counterpartCellIds?: string[];
  /**
   * counterpart 대상 fact identity key 목록. cellId 는 축만 다른 물리 셀끼리
   * 충돌할 수 있어, identityKey 가 있는 대응은 `data-cell-identity-key` 로
   * 좁혀 다른 축 셀이 함께 칠해지지 않게 한다.
   */
  counterpartIdentityKeys?: string[];
  /** 전체 매칭된 IXD 원본 cellId 목록. 미매칭 status 의 "매칭 제외" 집합으로 쓴다. */
  matchedCellIds?: string[];
  /** 매칭 DSD 가 없는 IXD 값 셀에 상시 status 면색을 입힐지 여부(검토 모드 전용). */
  showUnmatchedStatus?: boolean;
}

/**
 * 셀을 리렌더하지 않고 `<style>` element 를 document.head 에 주입해
 * `data-cell-id` / `data-cell-identity-key` 로 대상을 찾는다. 따라서 대상 셀이 이 속성을
 * 가진 `td` 여야 하고, 그 셀들을 감싸는 컨테이너 엘리먼트에 반환된 scopeId 를 `id` 로
 * 지정해야 한다(다른 렌더러 인스턴스나 다른 chunk 카드의 동일 cellId 와 섞이지 않도록
 * 스타일이 그 컨테이너 안으로만 한정된다).
 *
 * 입히는 두 시각은 `review-style-tokens.ts` 가 소유한다(색을 바꿀 땐 거기서 바꾼다).
 * - `counterpartCellIds` / `counterpartIdentityKeys` 에 담긴 셀 → `REVIEW_COUNTERPART_CSS` 외곽선.
 * - `showUnmatchedStatus` 가 true 면, 값이 있고 읽기전용이 아니며 `matchedCellIds` 에 없는
 *   (= 매칭되는 DSD 셀이 없는) 값 셀 → `REVIEW_NO_MATCH_CSS` 면색.
 *
 * @param options.counterpartCellIds - 외곽선을 입힐 IXD cellId 목록.
 * @param options.counterpartIdentityKeys - 외곽선을 입힐 fact identity key 목록. 축만 다른 물리 셀끼리 cellId 가 충돌할 때 identityKey 로 좁힌다.
 * @param options.matchedCellIds - 전체 매칭된 IXD cellId 목록. `showUnmatchedStatus` 판정에서 제외 집합으로 쓰인다.
 * @param options.showUnmatchedStatus - true 면 매칭 DSD 가 없는 값 셀에 상시 면색을 입힌다.
 * @returns scopeId - 대상 셀들을 감싸는 컨테이너 엘리먼트의 `id` 로 지정해야 하는 문자열.
 */
export function useIxdCellReviewStyles({
  counterpartCellIds,
  counterpartIdentityKeys,
  matchedCellIds,
  showUnmatchedStatus,
}: UseIxdCellReviewStylesOptions): string {
  const scopeId = `ixd-review-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;

  useEffect(() => {
    const styleElId = `${scopeId}-style`;
    const ids = counterpartCellIds ?? [];
    const identityKeys = counterpartIdentityKeys ?? [];
    const rules: string[] = [];

    const counterpartSelectors = [
      ...ids.map((id) => `#${scopeId} [data-cell-id="${CSS.escape(id)}"]`),
      ...identityKeys.map(
        (key) => `#${scopeId} [data-cell-identity-key="${CSS.escape(key)}"]`,
      ),
    ];
    if (counterpartSelectors.length > 0) {
      rules.push(
        `${counterpartSelectors.join(",")}{${REVIEW_COUNTERPART_CSS}}`,
      );
    }

    if (showUnmatchedStatus) {
      // 값이 있는 td 중, 편집불가(abstract)·빈 값·매칭된 셀을 제외한 나머지 = 매칭 DSD 없음.
      const exclude = (matchedCellIds ?? [])
        .map((id) => `:not([data-cell-id="${CSS.escape(id)}"])`)
        .join("");
      const selector =
        // ponytail: 값 0 제외는 CSS 정확매칭이라 "0"만 걸림. "0.00" 등 변형 생기면 JS 판정으로 전환.
        `#${scopeId} td[data-cell-id]:not([data-readonly]):not([data-raw-value=""]):not([data-raw-value="0"])` +
        exclude;
      rules.push(`${selector}{${REVIEW_NO_MATCH_CSS}}`);
    }

    let styleEl = document.getElementById(styleElId) as HTMLStyleElement | null;

    if (rules.length === 0) {
      if (styleEl) styleEl.textContent = "";
      return;
    }

    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = styleElId;
      document.head.appendChild(styleEl);
    }

    styleEl.textContent = rules.join("");
  }, [
    counterpartCellIds,
    counterpartIdentityKeys,
    matchedCellIds,
    showUnmatchedStatus,
    scopeId,
  ]);

  // 언마운트 시 강조용 <style> element 정리.
  useEffect(() => {
    const styleElId = `${scopeId}-style`;
    return () => {
      document.getElementById(styleElId)?.remove();
    };
  }, [scopeId]);

  return scopeId;
}
